import { createI18n } from 'vue-i18n';
import messages from '../messages';
import { get, ENDPOINTS } from '@/api/mainApi';
import dayjs from 'dayjs/esm/index.js';
import relativeTime from 'dayjs/esm/plugin/relativeTime/index.js';
import localizedFormat from 'dayjs/esm/plugin/localizedFormat/index.js';
import locale_sv from 'dayjs/esm/locale/sv.js';
import locale_nb from 'dayjs/esm/locale/nb.js';
import locale_de from 'dayjs/esm/locale/de.js';
import locale_en from 'dayjs/esm/locale/en.js';

function getSupportedLocale(): string {
  const supportedLocales = ['sv-SE', 'nb-NO', 'de-DE', 'en'];
  const browserLanguages =
    navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];

  for (const browserLanguage of browserLanguages) {
    const browserLanguageIndex: number = browserLanguages.indexOf(browserLanguage);
    const normalizedBrowserLanguage: string = browserLanguage.toLowerCase();

    const exactMatch: string | undefined = supportedLocales.find(
      (locale) => locale.toLowerCase() === normalizedBrowserLanguage
    );
    if (hasValidLanguageMatch(exactMatch, browserLanguageIndex)) {
      return exactMatch!;
    }
    const partialMatch: string | undefined = supportedLocales.find(
      (locale) => locale.toLowerCase().indexOf(normalizedBrowserLanguage) > -1
    );
    if (hasValidLanguageMatch(partialMatch, browserLanguageIndex)) {
      return partialMatch!;
    }
    const languageOnly: string = normalizedBrowserLanguage.split('-')[0];
    const languageMatch: string | undefined = supportedLocales.find(
      (locale) => locale.split('-')[0].toLowerCase() === languageOnly
    );
    if (hasValidLanguageMatch(languageMatch, browserLanguageIndex)) {
      return languageMatch!;
    }
  }

  return 'sv-SE';
}

function hasValidLanguageMatch(
  matchingLocale: string | undefined,
  browserLanguageIndex: number
): boolean {
  // english is often included by default in the browser languages (but not at the top)
  // so we only want to prioritize it if it's the first language in the list
  if (!!matchingLocale) {
    return matchingLocale !== 'en' || (matchingLocale === 'en' && browserLanguageIndex === 0);
  }
  return false;
}

const supportedLocale = getSupportedLocale();
const minimumTranslationLength = 30; // counts json nodes / categories

type LocaleKey = 'sv-SE' | 'nb-NO' | 'de-DE' | 'en';

function mergeLocaleMessages(base: unknown, overlay: unknown): unknown {
  if (overlay === null || overlay === undefined) {
    return base;
  }
  if (
    typeof overlay !== 'object' ||
    Array.isArray(overlay) ||
    typeof base !== 'object' ||
    base === null ||
    Array.isArray(base)
  ) {
    return overlay;
  }
  const merged: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, value] of Object.entries(overlay as Record<string, unknown>)) {
    merged[key] = mergeLocaleMessages(merged[key], value);
  }
  return merged;
}

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

if (supportedLocale === 'sv-SE') {
  dayjs.locale(locale_sv);
} else if (supportedLocale === 'nb-NO') {
  dayjs.locale(locale_nb);
} else if (supportedLocale === 'de-DE') {
  dayjs.locale(locale_de);
} else if (supportedLocale === 'en') {
  dayjs.locale(locale_en);
} else {
  dayjs.locale(locale_sv);
}

// $date will be set on app.config.globalProperties in main.ts

const dateTimeFormats: { [key: string]: Intl.DateTimeFormatOptions } = {
  weekDay: { weekday: 'long' },
  yearMonth: { year: 'numeric', month: 'long' },
};

const numberFormats = {
  'sv-SE': {
    currency: {
      style: 'currency',
      currency: 'SEK',
    },
    currencyInteger: {
      style: 'currency',
      currency: 'SEK',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
  'nb-NO': {
    currency: {
      style: 'currency',
      currency: 'NOK',
      useGrouping: true,
    },
    currencyInteger: {
      style: 'currency',
      currency: 'NOK',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
  'de-DE': {
    currency: {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
    },
    currencyInteger: {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
  en: {
    currency: {
      style: 'currency',
      currency: 'SEK',
    },
    currencyInteger: {
      style: 'currency',
      currency: 'SEK',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  },
} as const;

// Create VueI18n instance with options
const vueI18n = createI18n({
  legacy: false, // Explicitly disable legacy mode for Vue 3
  locale: supportedLocale,
  messages,
  dateTimeFormats: {
    'sv-SE': dateTimeFormats,
    'nb-NO': dateTimeFormats,
    'de-DE': dateTimeFormats,
  },
  numberFormats,
});

async function loadTranslations(locale: string): Promise<void> {
  locale = locale || getSupportedLocale();
  const localeKey = locale as LocaleKey;
  const bundled = messages[locale];

  try {
    const localeMessages = await get(
      `${ENDPOINTS.TRANSLATIONS}/${ENDPOINTS.AS_JSON}/${locale}`,
      undefined,
      false
    );

    if (
      localeMessages &&
      typeof localeMessages === 'object' &&
      Object.keys(localeMessages).length > minimumTranslationLength
    ) {
      const merged = mergeLocaleMessages(bundled, localeMessages);
      vueI18n.global.setLocaleMessage(localeKey, merged as typeof bundled);
      return;
    }
  } catch {
    // bundled locale files remain in use
  }

  if (bundled) {
    vueI18n.global.setLocaleMessage(localeKey, bundled);
  }
}

function getUserLanguage(): string {
  return vueI18n.global.locale.value;
}
async function setLanguage(locale: string): Promise<void> {
  if (locale !== vueI18n.global.locale.value) {
    if (locale === 'sv-SE') {
      dayjs.locale(locale_sv);
    } else if (locale === 'nb-NO') {
      dayjs.locale(locale_nb);
    } else if (locale === 'de-DE') {
      dayjs.locale(locale_de);
    } else if (locale === 'en') {
      dayjs.locale(locale_en);
    } else {
      dayjs.locale(locale_sv);
    }

    vueI18n.global.locale.value = locale as 'sv-SE' | 'nb-NO' | 'de-DE' | 'en';
    await loadTranslations(locale);
  }
}

export default {
  vueI18n,
  getUserLanguage,
  getSupportedLocale,
  loadTranslations,
  setLanguage,
};
